import { useCallback, useEffect, useState } from "react";
import { STATUS } from "@/constants";
import { fetchAppTabs, type AppTabSummary } from "@/features/app-studio/api/appTabsApi";

export type AppTabsStatus = (typeof STATUS)[keyof typeof STATUS];

export type UseAppTabsResult = {
  tabs: AppTabSummary[];
  status: AppTabsStatus;
  error: string | null;
  reload: () => Promise<void>;
};

/**
 * Loads an app's tabs. Reloads whenever `refreshSignal` changes so callers can force a
 * refetch after a tab is created/renamed/deleted/reordered.
 */
export function useAppTabs(appId: number | null, refreshSignal?: number): UseAppTabsResult {
  const [tabs, setTabs] = useState<AppTabSummary[]>([]);
  const [status, setStatus] = useState<AppTabsStatus>(STATUS.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [loadedAppId, setLoadedAppId] = useState<number | null>(appId);

  // Tabs are app-scoped, so a switch to another app drops the loaded tabs in the same
  // render: callers must never see one app's tabs while another app's are loading. A
  // `refreshSignal` reload keeps the current tabs visible until the new ones arrive.
  if (loadedAppId !== appId) {
    setLoadedAppId(appId);
    setTabs([]);
    setStatus(STATUS.LOADING);
    setError(null);
  }

  const reload = useCallback(async () => {
    if (appId === null) {
      setTabs([]);
      setStatus(STATUS.SUCCEEDED);
      setError(null);
      return;
    }
    setStatus(STATUS.LOADING);
    setError(null);
    try {
      const rows = await fetchAppTabs(appId);
      setTabs(rows);
      setStatus(STATUS.SUCCEEDED);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load tabs.");
      setStatus(STATUS.FAILED);
    }
  }, [appId]);

  useEffect(() => {
    void reload();
  }, [reload, refreshSignal]);

  return { tabs, status, error, reload };
}
